"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Icon } from "@repo/ui/icon";
import { absoluteCmsUrl, BaseImage } from "../base-image/BaseImage";
import Link from "next/link";
import Image from "next/image";

export type StrapiImageFormat = {
  ext: string;
  url: string;
  width: number;
  height: number;
  mime: string;
  name: string;
  size: number;
};

export type StrapiMedia = {
  id: number;
  url: string;
  width: number;
  height: number;
  name: string;
  alternativeText?: string | null;
  caption?: string | null;
  formats?: {
    large?: StrapiImageFormat;
    medium?: StrapiImageFormat;
    small?: StrapiImageFormat;
    thumbnail?: StrapiImageFormat;
  };
};

export type LinkHotelsItem = {
  id: number;
  title: string;
  url: string;
  images: StrapiMedia[];
};

export type LinkHotelsHeading = {
  id: number;
  title: string;
  level: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
};

export type LinkHotelsProps = {
  heading?: LinkHotelsHeading | null;
  listImages: LinkHotelsItem[];
};

gsap.registerPlugin(ScrollTrigger);

const COMING_SOON: Array<{ name: string; note?: string }> = [
  { name: "Lucerne", note: "Coming soon" },
  { name: "Basel", note: "Coming soon" },
];

export default function HotelRevealGallery({
  heading,
  listImages,
}: LinkHotelsProps) {
  const [activeHotelIndex, setActiveHotelIndex] = useState(0);
  const imageWrappers = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const hotels = useMemo(() => {
    return (listImages || []).map((h) => {
      const imgs = (h.images || []).map((img) => {
        const src =
          img.formats?.medium?.url || img.formats?.small?.url || img.url;
        return {
          src: absoluteCmsUrl(src),
          alt: img.alternativeText || img.caption || img.name,
          width: img.width,
          height: img.height,
        };
      });
      return { name: h.title, url: h.url, images: imgs };
    });
  }, [listImages]);

  const positions = useMemo(
    () => [
      { top: "5%", left: "0%", width: 174, height: 265 },
      { top: "22%", left: "5%", width: 204, height: 136 },
      { bottom: "28%", left: "0%", width: 283, height: 189 },
      { bottom: "15%", left: "10%", width: 236, height: 157 },
      { top: "5%", right: "1%", width: 283, height: 188 },
      { top: "28%", right: "15%", width: 170, height: 259 },
      { bottom: "20%", right: "20%", width: 122, height: 186 },
      { bottom: "8%", right: "0%", width: 283, height: 189 },
    ],
    [],
  );

  const changeImages = useCallback(
    (newIndex: number) => {
      const max = hotels[0]?.images.length ?? 0;
      if (!max) return;

      for (let i = 0; i < max; i++) {
        const wrapper = imageWrappers.current[i];
        if (!wrapper) continue;

        const images = wrapper.querySelectorAll("img");
        const newImg = images[newIndex] as HTMLImageElement | undefined;
        const currentImg = Array.from(images).find((img) =>
          img.classList.contains("visible"),
        ) as HTMLImageElement | undefined;

        if (currentImg && newImg && currentImg !== newImg) {
          gsap.to(currentImg, {
            opacity: 0,
            duration: 0.3,
            onComplete: () => currentImg.classList.remove("visible"),
          });

          gsap.fromTo(newImg, { opacity: 0 }, { opacity: 1, duration: 0.3 });
          newImg.classList.add("visible");
        }
      }
      setActiveHotelIndex(newIndex);
    },
    [hotels],
  );

  useEffect(() => {
    imageWrappers.current.forEach((wrapper, i) => {
      if (!wrapper) return;
      // Add GPU acceleration class
      wrapper.classList.add("parallax-element");

      gsap.to(wrapper, {
        y: i % 2 === 0 ? -50 : 50,
        scrollTrigger: {
          trigger: containerRef.current!,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5, // Slightly smoother scrub
          fastScrollEnd: true, // Better performance
        },
      });
    });
  }, [positions.length, hotels.length]);

  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    imageWrappers.current.forEach((wrapper, i) => {
      if (!wrapper) return;

      const offsetX = ((clientX - centerX) / centerX) * (i % 2 === 0 ? -3 : 3);
      const offsetY = ((clientY - centerY) / centerY) * (i % 2 === 0 ? -3 : 3);

      gsap.to(wrapper, {
        x: offsetX,
        y: `+=${offsetY}`, // additive to scroll motion
        duration: 0.4, // Slightly faster
        ease: "power2.out",
        force3D: true, // Force GPU acceleration
      });
    });
  };

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative reveal-stagger flex md:min-h-[900px] overflow-hidden px-4 pt-12 md:pb-32 pb-20"
      aria-label="Hotel hover gallery"
      id="hotels"
    >
      {/* Center text */}
      <div className="max-w-3xl mx-auto flex flex-col md:gap-20 gap-14 text-center">
        <div className="flex flex-col gap-4 items-center md:mt-20">
          <div className="flex flex-row gap-2 py-2 px-4 bg-[#E1DDD6] rounded-full">
            <Image
              src="/assets/lms-group-icon.svg"
              alt="lms group icon"
              width={16}
              height={16}
            />
            <span className="text-[#5C5956] font-medium">
              La Maison Suisse Group
            </span>
          </div>
          {heading?.title && (
            <p className="text-grey-200 md:text-base text-sm reveal-item">
              {heading.title}
            </p>
          )}
        </div>
        <ul
          className="flex flex-col reveal-item gap-4 xl:text-6xl text-5xl text-grey-950 items-center"
          role="list"
          aria-label="Hotels"
        >
          {hotels.map((hotel, i) => (
            <li
              key={hotel.name}
              className={`cursor-pointer transition-colors ${i === activeHotelIndex ? "text-black" : "text-neutral-400"} hover:text-black`}
            >
              {hotel.url?.startsWith("http") ? (
                <a
                  href={hotel.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex group reveal-item tracking-tighter"
                  onMouseEnter={() => changeImages(i)}
                  onFocus={() => changeImages(i)}
                  aria-label={`Open ${hotel.name} in new tab`}
                >
                  {hotel.name}
                  <div className="absolute -right-18 md:group-hover:opacity-100 md:opacity-0 transition ml-2 py-1 px-2 rounded-full h-fit bg-[#E1DDD6] flex flex-row gap-0.5 items-center text-[#5C5956] font-medium">
                    <span className="text-sm md:text-base">LMS</span>
                    <Icon name="arrow-up-right" size={"20"} />
                  </div>
                </a>
              ) : (
                <Link
                  href={hotel.url || "#"}
                  className="flex group reveal-item"
                  onMouseEnter={() => changeImages(i)}
                  onFocus={() => changeImages(i)}
                  aria-label={`Open ${hotel.name}`}
                >
                  {hotel.name}
                  <Icon
                    name="arrow-up-right"
                    size={"30"}
                    className="md:group-hover:opacity-100 md:opacity-0 transition"
                  />
                </Link>
              )}
            </li>
          ))}
          {COMING_SOON.map((item, i) => (
            <li
              className="group tracking-tight text-neutral-400 reveal-item hover:text-grey-900 flex flex-row relative"
              key={`coming-soon-${item.name}-${i}`}
            >
              {item.name}
              <span className="absolute text-sm md:text-base -right-28 md:group-hover:opacity-100 md:opacity-0 transition text-[#5C5956] font-medium bg-berzh py-1 px-2 self-start rounded-full h-auto">
                {item.note}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Floating Image Slots */}
      {positions.map((style, index) => (
        <div
          key={index}
          ref={(el) => {
            imageWrappers.current[index] = el;
          }}
          className="absolute overflow-hidden md:block hidden"
          style={{
            ...style,
            width: `${style.width}px`,
            height: `${style.height}px`,
          }}
        >
          {hotels.map((hotel, hIndex) => (
            <BaseImage
              key={`${hIndex}-${index}`}
              src={hotel.images[index]?.src || ""}
              alt={
                hotel.images[index]?.alt || `${hotel.name} image ${index + 1}`
              }
              width={style.width}
              height={style.height}
              className={`absolute top-0 left-0 h-full w-full object-cover transition-opacity duration-500 ${hIndex === activeHotelIndex ? "opacity-100 visible" : "opacity-0"}`}
              loading={hIndex === 0 ? "eager" : "lazy"}
              decoding="async"
            />
          ))}
        </div>
      ))}
    </section>
  );
}
